import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fileSrc } from "@/lib/resources";
import { ResourcesGate } from "@/components/resources/ResourcesGate";

const Lightbox = ({ photos, index, setIndex }) => {
  const p = photos[index];
  const go = (d) => setIndex((index + d + photos.length) % photos.length);
  return <Dialog open={index !== null} onOpenChange={(o) => { if (!o) setIndex(null); }}>
    <DialogContent className="max-w-5xl w-[calc(100%-1rem)] bg-[#111] border-0 p-2 pt-12 sm:p-4 sm:pt-12 [&>button]:text-white" data-testid="album-lightbox">
      <DialogTitle className="sr-only">Photo {index + 1}</DialogTitle>
      {p && <>
        <img src={fileSrc(p.url)} alt={p.caption || "Gallery photo"} className="w-full max-h-[75vh] object-contain" data-testid="album-lightbox-image" />
        <div className="flex items-center justify-between gap-3 text-white text-sm mt-2">
          <button onClick={() => go(-1)} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-[#E60000] transition-colors" aria-label="Previous photo" data-testid="album-lightbox-prev"><ChevronLeft className="w-5 h-5" /></button>
          <span className="text-center" data-testid="album-lightbox-caption">{p.caption || `${index + 1} of ${photos.length}`}</span>
          <button onClick={() => go(1)} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-[#E60000] transition-colors" aria-label="Next photo" data-testid="album-lightbox-next"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </>}
    </DialogContent>
  </Dialog>;
};

const AlbumBody = () => {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState("");
  const [index, setIndex] = useState(null);
  useEffect(() => { api.get(`/resources/albums/${id}`).then((r) => setA(r.data)).catch((e) => setError(errMsg(e))); }, [id]);
  return <div data-testid="album-page">
    <Link to="/resources?tab=gallery" className="inline-flex items-center gap-1.5 text-sm font-semibold hover:text-[#E60000]" data-testid="album-back"><ArrowLeft className="w-4 h-4" />Gallery</Link>
    {error ? <p role="alert" className="text-sm text-[#B30000] mt-6" data-testid="album-error">{error}</p> : !a ? <p className="text-gray-500 mt-6">Loading…</p> : <>
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-4" data-testid="album-title">{a.title}</h1>
      <p className="text-sm text-gray-600 mt-2">{a.event_title ? `${a.event_title} · ` : ""}{a.photos.length} photo{a.photos.length === 1 ? "" : "s"}</p>
      {a.description && <p className="text-sm sm:text-base text-gray-700 mt-3 max-w-2xl">{a.description}</p>}
      {a.photos.length ? <div className="columns-2 md:columns-3 lg:columns-4 gap-3 mt-8" data-testid="album-photos">
        {a.photos.map((p, i) => <button key={p.id} onClick={() => setIndex(i)} className="block w-full mb-3 overflow-hidden rounded-md bg-[#F3F4F6] break-inside-avoid" data-testid={`album-photo-${i}`}>
          <img src={fileSrc(p.url)} alt={p.caption || "Gallery photo"} loading="lazy" className="w-full hover:scale-105 transition-transform duration-500" />
        </button>)}
      </div> : <p className="text-sm text-gray-500 mt-8" data-testid="album-empty">No photos in this album yet.</p>}
      <Lightbox photos={a.photos} index={index} setIndex={setIndex} />
    </>}
  </div>;
};

export default function ResourceAlbum() {
  const { id } = useParams();
  return <ResourcesGate next={`/resources/gallery/${id}`}><AlbumBody /></ResourcesGate>;
}
